import React, { useRef, useEffect, useState, useMemo } from "react";
import { CryptoAlertData, PositionData } from "@crypto-trading/common/browser";
import { TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { Drawer } from "vaul";
import { PositionChart } from "./PositionChart";
import {
  TradingViewChartWidget,
  useTradingViewSymbol,
} from "@crypto-trading/ui-shared/client";
import { useAlertPriceData } from "@/hooks/useAlertPriceData";
import { PositionDetailDrawer } from "./PositionDetailDrawer";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import { TokenAnalysisSection } from "./TokenAnalysisSection";
import { trackEvent } from "@/utils/analytics";
import { useTokenAvailability } from "@/hooks/useMarketData";
import { useUserExchanges } from "@/hooks/useExchanges";
import { ensureUsdtSymbol } from "@/utils/symbols";
import { usePreviousAlerts } from "@/hooks/useCryptoAlerts";
import { TokenIcon } from "./TokenIcon";
import { AlertHistory } from "./AlertHistory";

interface AlertDetailDrawerProps {
  userId: string;
  alert: CryptoAlertData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrade?: (alert: CryptoAlertData) => void;
}

export const AlertDetailDrawer: React.FC<AlertDetailDrawerProps> = ({
  userId,
  alert,
  open,
  onOpenChange,
  onTrade,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const [isLivePrice, setIsLivePrice] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<PositionData | null>(
    null
  );
  const [positionDrawerOpen, setPositionDrawerOpen] = useState(false);

  const isListing = alert?.type === "new_coin";

  // Normalize symbol for token availability check
  const normalizedSymbol = useMemo(
    () => (alert?.currency ? ensureUsdtSymbol(alert.currency) : ""),
    [alert?.currency]
  );

  // Check token availability and user exchanges
  const { data: exchanges = [], isLoading: isLoadingExchanges } =
    useUserExchanges(userId, true);

  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, normalizedSymbol, open && !!normalizedSymbol);

  // Backend returns all globally available exchanges in userExchanges field
  const globallyAvailableExchangeCodes = useMemo(() => {
    return tokenAvailability?.userExchanges ?? [];
  }, [tokenAvailability?.userExchanges]);

  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes]
  );

  // Filter user's connected exchanges to only those that support the token
  const supportedUserExchangeCodes = useMemo(() => {
    return exchanges
      .map((e) => e.code)
      .filter((code) => globallyAvailableSet.has(code));
  }, [exchanges, globallyAvailableSet]);

  const hasTradableExchange = supportedUserExchangeCodes.length > 0;
  const isCheckingAvailability = isLoadingAvailability || isLoadingExchanges;

  // Fetch previous alerts (listings and delistings) for this currency
  const { data: previousAlerts = [] } = usePreviousAlerts(
    userId,
    alert?.currency || "",
    alert?.id || "",
    open && !!alert
  );

  // Subscribe to live price updates when drawer is open
  const { price: livePrice, isSubscribed } = useTokenLivePrice(
    alert?.currency || "",
    "dynamic",
    open && isListing && !!alert // Only subscribe for listing alerts when drawer is open
  );

  // Track if we've waited too long for a price (timeout after 8 seconds)
  const [priceLoadTimeout, setPriceLoadTimeout] = useState(false);

  useEffect(() => {
    if (open && isListing && !livePrice) {
      setPriceLoadTimeout(false);
      const timer = setTimeout(() => {
        setPriceLoadTimeout(true);
      }, 8000); // 8 second timeout

      return () => clearTimeout(timer);
    }
  }, [open, isListing, livePrice]);

  // Use live price from WebSocket subscription
  const displayPrice = livePrice ?? null;

  const {
    enabledIntervals,
    selectedInterval,
    setSelectedInterval,
    priceChanges,
    isLoading,
    isLoadingMore,
    error: candleError,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
    symbol,
    isTokenUnavailable,
    historicalChartUnavailable,
  } = useAlertPriceData({
    userId,
    alert,
    open,
  });

  const isPriceLoading =
    open && isListing && !livePrice && !priceLoadTimeout && !isTokenUnavailable;

  const {
    tradingViewSymbol,
    isLoading: isResolvingLiveChart,
    isError: isLiveChartMissing,
  } = useTradingViewSymbol(symbol);
  const liveChartReady = Boolean(tradingViewSymbol) && !isLiveChartMissing;
  const showIntervalChart =
    !historicalChartUnavailable &&
    (isLoading || priceChanges.length > 0 || Boolean(candleError));

  // Reset live price toggle when drawer closes
  useEffect(() => {
    if (!open) {
      setIsLivePrice(false);
      setPositionDrawerOpen(false);
      setSelectedPosition(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && alert) {
      trackEvent("alert_drawer_view", {
        userId,
        alertId: alert.id,
        symbol: alert.currency,
        alertType: alert.type,
        tier: alert.tier ?? null,
        exchange: alert.exchange,
      });
    }
  }, [open, alert]);

  // Restore scroll position after interval/data changes
  useEffect(() => {
    if (scrollContainerRef.current && scrollPositionRef.current > 0) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [selectedInterval, isLoading, priceChanges.length]);

  if (!alert || !open) {
    return null;
  }

  const formatPrice = (price: number | null) => {
    if (!price)
      return isTokenUnavailable
        ? "Not available at supported exchanges"
        : "Not available";
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
  };

  const calculatePriceChange = () => {
    if (!alert?.price || !displayPrice) return null;
    const change = ((displayPrice - alert.price) / alert.price) * 100;
    return change;
  };

  const formatDateTime = (date: Date) => {
    const d = new Date(date);
    return d.toLocaleString(undefined, {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h24",
    });
  };

  const priceChange = calculatePriceChange();

  const getTierBadgeColor = () => {
    if (alert.tier === 1) return "text-yellow-400";
    if (alert.tier === 2) return "text-blue-400";
    return "text-gray-400";
  };

  const tierBadgeColor = getTierBadgeColor();

  const handleViewPosition = () => {
    if (alert.userPosition) {
      // Convert UserPosition to PositionData format
      const positionData: PositionData = {
        positionId: alert.userPosition.id,
        symbol: alert.userPosition.symbol,
        positionType: alert.userPosition.positionType as "LONG" | "SHORT",
        amount: alert.userPosition.amount.toString(),
        leverage: alert.userPosition.leverage.toString(),
        entryPrice: alert.userPosition.entryPrice.toString(),
        markPrice: alert.userPosition.markPrice?.toString() || "0",
        exitPrice: alert.userPosition.exitPrice?.toString() || "0",
        pnl: alert.userPosition.realizedPnl?.toString() || "0",
        pnlPercentage: alert.userPosition.pnlPercentage?.toString() || "0",
        exchangeCode: alert.exchange,
        exchangeName: alert.exchange,
        usdAmount: alert.userPosition.usdAmount.toString(),
        openedAt: alert.userPosition.openedAt?.toString() || undefined,
        closedAt: alert.userPosition.closedAt?.toString() || undefined,
      };
      setSelectedPosition(positionData);
      setPositionDrawerOpen(true);
    }
  };

  return (
    <>
      <Drawer.Root
        open={open}
        onOpenChange={onOpenChange}
        repositionInputs={false}
      >
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[100]" />
          <Drawer.Content className="bg-[#1a1b20] flex rounded-t-xl flex-col fixed bottom-0 left-0 right-0 max-h-[90vh] z-[101]">
            {/* Header */}
            <div className="mx-4 border-b border-gray-500/30 pb-3 pt-2 sticky top-0 bg-secondary z-10">
              <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
              <h2 className="text-xs text-text-primary text-center">
                Alert Details
              </h2>
              <div className="flex items-center gap-2 mt-3">
                {isListing ? (
                  <TrendingUp className="w-5 h-5 text-green-400" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-400" />
                )}
                <h2 className="text-md font-bold text-text-primary">
                  {alert.currency}
                </h2>
                <TokenIcon
                  symbol={alert.currency}
                  logo={alert.tokenMetadata?.logo}
                  size="sm"
                />
                <span className="px-2 py-0.5 rounded-full text-xs bg-black/50">
                  {alert.type === "new_coin" ? "Listing" : "Delisting"}
                </span>
                {alert.hasAutotraded && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-accent/10">
                    Autotraded 🤖
                  </span>
                )}
                {alert.tier && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs bg-black/50 ${tierBadgeColor}`}
                  >
                    Tier {"🔥".repeat(4 - alert.tier)}
                  </span>
                )}
              </div>
            </div>

            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto space-y-4"
            >
              {/* Content */}
              <div className="p-4 space-y-6">
                {/* Alert Info Grid */}
                <div className="space-y-4 p-3">
                  {/* Row 1: Exchange | ExchangeType */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-text-secondary mb-1">
                        Exchange
                      </p>
                      <p className="text-sm text-text-primary capitalize">
                        {alert.exchange}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-text-secondary mb-1">
                        Exchange Type
                      </p>
                      <p className="text-sm text-text-primary capitalize">
                        {alert.exchangeType || "spot"}
                      </p>
                    </div>
                  </div>

                  {/* Row 2: Alert Price | Current Price */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-text-secondary mb-1">
                        Alert Price
                      </p>
                      <p className="text-sm text-text-primary">
                        {formatPrice(alert.price)}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <p className="text-xs text-text-secondary">
                          Current Price
                        </p>
                        {isSubscribed && livePrice && (
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                          </span>
                        )}
                      </div>
                      {isPriceLoading ? (
                        <div className="space-y-1">
                          <div className="h-5 bg-gray-600/30 rounded animate-pulse w-24"></div>
                          <div className="h-4 bg-gray-600/30 rounded animate-pulse w-16"></div>
                        </div>
                      ) : (
                        <>
                          <p
                            className={`text-sm text-text-primary ${isSubscribed && livePrice ? "transition-all duration-150" : ""}`}
                          >
                            {formatPrice(displayPrice)}
                          </p>
                          {priceChange !== null && (
                            <p
                              className={`text-xs mt-0.5 ${priceChange >= 0 ? "text-green-400" : "text-red-400"}`}
                            >
                              {priceChange >= 0 ? "+" : ""}
                              {priceChange.toFixed(2)}%
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Row 4: Source Link and Alert Time */}
                  <div className="grid grid-cols-2 gap-4">
                    {alert.source && (
                      <div>
                        <p className="text-xs text-text-secondary mb-1">Meta</p>
                        <a
                          href={alert.source}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-accent hover:underline break-all underline"
                        >
                          Source
                        </a>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-text-secondary mb-1">
                        Alert Time
                      </p>
                      <p className="text-sm text-text-primary">
                        {formatDateTime(alert.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Price Chart */}
                <div>
                  {showIntervalChart ? (
                    isLivePrice && liveChartReady ? (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-text-primary">
                          Price Chart
                        </h3>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs text-text-secondary">
                            Live Chart
                          </span>
                          <div className="relative inline-block w-10 h-5">
                            <input
                              type="checkbox"
                              checked={isLivePrice}
                              onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsLivePrice(e.target.checked);
                                if (scrollContainerRef.current) {
                                  scrollPositionRef.current =
                                    scrollContainerRef.current.scrollTop;
                                }
                              }}
                              className="sr-only"
                            />
                            <div
                              className={`block w-10 h-5 rounded-full transition-colors ${
                                isLivePrice ? "bg-accent" : "bg-gray-600"
                              }`}
                            />
                            <div
                              className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${
                                isLivePrice ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </div>
                        </label>
                      </div>
                      <TradingViewChartWidget symbol={symbol} />
                    </>
                  ) : (
                    <PositionChart
                      priceChanges={priceChanges}
                      entryPrice={alert.price}
                      isProfitable={
                        alert.userPosition?.pnlPercentage !== null &&
                        alert.userPosition?.pnlPercentage !== undefined &&
                        alert.userPosition.pnlPercentage >= 0
                      }
                      openedAt={alert.createdAt.toString()}
                      markerText="Triggered"
                      entryMarkerVariant="callout"
                      onRequestOlderCandles={loadOlderCandles}
                      onRequestNewerCandles={loadNewerCandles}
                      hasMoreHistory={hasMoreHistory}
                      hasMoreFuture={hasMoreFuture}
                      onFollowLiveChange={setFollowLive}
                      viewportResetKey={`${selectedInterval}-${alert?.id ?? ""}`}
                      enabledIntervals={
                        priceChanges.length > 0 ? enabledIntervals : []
                      }
                      selectedInterval={selectedInterval}
                      onIntervalChange={setSelectedInterval}
                      onIntervalClick={() => {
                        if (scrollContainerRef.current) {
                          scrollPositionRef.current =
                            scrollContainerRef.current.scrollTop;
                        }
                      }}
                      isLoading={isLoading}
                      isLoadingMore={isLoadingMore}
                      error={candleError}
                      headerActions={
                        liveChartReady ? (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <span className="text-xs text-text-secondary">
                              Live Chart
                            </span>
                            <div className="relative inline-block w-10 h-5">
                              <input
                                type="checkbox"
                                checked={isLivePrice}
                                onChange={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsLivePrice(e.target.checked);
                                  if (scrollContainerRef.current) {
                                    scrollPositionRef.current =
                                      scrollContainerRef.current.scrollTop;
                                  }
                                }}
                                className="sr-only"
                              />
                              <div
                                className={`block w-10 h-5 rounded-full transition-colors ${
                                  isLivePrice ? "bg-accent" : "bg-gray-600"
                                }`}
                              />
                              <div
                                className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${
                                  isLivePrice ? "translate-x-5" : "translate-x-0"
                                }`}
                              />
                            </div>
                          </label>
                        ) : undefined
                      }
                    />
                  )
                ) : isResolvingLiveChart ? (
                    <>
                      <h3 className="text-sm font-bold text-text-primary mb-3">
                        Price Chart
                      </h3>
                      <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg">
                        <Loader2 className="w-6 h-6 animate-spin text-accent" />
                      </div>
                    </>
                  ) : liveChartReady ? (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-text-primary">
                          Price Chart
                        </h3>
                        <span className="text-xs text-text-secondary">Live</span>
                      </div>
                      <TradingViewChartWidget symbol={symbol} />
                    </>
                  ) : null}
                </div>

                {/* Previous Alerts Section */}
                <AlertHistory
                  alerts={previousAlerts}
                  variant="drawer"
                  description="This token was listed/delisted before on:"
                />

                {/* Token Metadata Section */}
                {alert.tokenMetadata &&
                  (alert.tokenMetadata.description ||
                    alert.tokenMetadata.urls) && (
                    <div className="space-y-3 bg-primary/40 border border-gray-500/30 rounded-lg p-3">
                      <h3 className="text-sm font-bold text-text-primary">
                        Token Information
                      </h3>

                      {alert.tokenMetadata.description && (
                        <div>
                          <p className="text-xs text-text-secondary mb-1">
                            Description
                          </p>
                          <p className="text-sm text-text-primary leading-relaxed">
                            {alert.tokenMetadata.description}
                          </p>
                        </div>
                      )}

                      {alert.tokenMetadata.urls && (
                        <div>
                          <p className="text-xs text-text-secondary mb-2">
                            Official Links
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {alert.tokenMetadata.urls.website?.[0] && (
                              <a
                                href={alert.tokenMetadata.urls.website[0]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                              >
                                Website
                              </a>
                            )}
                            {alert.tokenMetadata.urls.twitter?.[0] && (
                              <a
                                href={alert.tokenMetadata.urls.twitter[0]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                              >
                                Twitter
                              </a>
                            )}
                            {alert.tokenMetadata.urls.source_code?.[0] && (
                              <a
                                href={alert.tokenMetadata.urls.source_code[0]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                              >
                                GitHub
                              </a>
                            )}
                            {alert.tokenMetadata.urls.technical_doc?.[0] && (
                              <a
                                href={alert.tokenMetadata.urls.technical_doc[0]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                              >
                                Docs
                              </a>
                            )}
                            {alert.tokenMetadata.urls.explorer?.[0] && (
                              <a
                                href={alert.tokenMetadata.urls.explorer[0]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                              >
                                Explorer
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}


                <TokenAnalysisSection tokenSymbol={alert.currency} />
              </div>
            </div>

            {/* Action Buttons */}
            {(alert.userPosition ||
              (onTrade && (hasTradableExchange || isCheckingAvailability))) && (
              <div className="sticky bottom-0 bg-[#1a1b20] border-t border-gray-500/30 p-4 space-y-2">
                {onTrade && hasTradableExchange && !alert.userPosition && (
                  <button
                    type="button"
                    onClick={() => onTrade(alert)}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-accent text-white hover:bg-accent/80 transition-colors"
                  >
                    Trade
                  </button>
                )}
                {alert.userPosition && (
                  <button
                    type="button"
                    onClick={handleViewPosition}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                  >
                    View Position
                  </button>
                )}
              </div>
            )}
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* Nested Position Detail Drawer */}
      {selectedPosition && (
        <PositionDetailDrawer
          userId={userId}
          position={selectedPosition}
          open={positionDrawerOpen}
          onOpenChange={setPositionDrawerOpen}
          nested={true}
          source="alert_nested"
        />
      )}
    </>
  );
};
